'use client';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useRef } from 'react';
import useDeviceTypeMobile from '@/src/hooks/useDeviceTypeMobile.jsx';

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

// Your existing function (or import it)


function getHeaderOffset(id) {
  if (typeof window === 'undefined') return 0;

  const isDesktop = window.innerWidth > 767;

  // Customize offsets for each section
  if (id === 'services') return isDesktop ? 180 : 0;
  if (id === 'products') return isDesktop ? 25 : 0;

  // Default header offset
  return isDesktop ? 0 : 0;
}
function smoothScrollToId(id, value = 0) {
  if (!id) return;
  if (typeof window === 'undefined') return;

  const isDesktop = window.innerWidth > 767;
  const element = document.getElementById(id);
  if (!element) return;

  const offset = getHeaderOffset(id) ;
  const elementTop = element.getBoundingClientRect().top + window.scrollY + offset + value;

  if (isDesktop) {
    gsap.killTweensOf(window);
    gsap.to(window, {
      duration: 0,
      scrollTo: { y: elementTop },
      ease: 'power2.out',
    });
  } else {
    // Mobile: Native instant scroll works fine
    window.scrollTo({ top: elementTop, behavior: 'auto' });
  }
}

// Exported click handler — use this on your anchor click events
export const smoothScrollTo = (targetId) => (e) => {
  e.preventDefault();
  if (typeof window === 'undefined') return;

  const id = targetId || e.target.closest('a[href^="#"]')?.getAttribute('href').slice(1);
  if (!id) return;

  window.history.pushState(null, '', `#${id}`);

  // Call the unified scroll method
  smoothScrollToId(id, 0);
};

export function useSmoothScrollOnRouteChange(pathname) {
  const timeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const isMobile = useDeviceTypeMobile()

  // When pathname changes, scroll to hash if present or scroll to top
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hash = window.location.hash.slice(1);
    const isDesktop = window.innerWidth > 767;

    if (!hash) {
      // No hash - scroll top only on route changes, not initial load
      if (!isInitialMount.current) {
        window.scrollTo(0, 0);
      }
      isInitialMount.current = false;
      return;
    }

    // Clear previous timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // Scroll to top immediately to avoid jump
    window.scrollTo(0, 0);

    timeoutRef.current = setTimeout(() => {
      smoothScrollToId(hash);
    }, 100);

    isInitialMount.current = false;

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [pathname]);

  // Handle hashchange events separately — for user manual hash changes on same page
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleHashChange = () => {
      const newHash = window.location.hash.slice(1);
      if (!newHash) return;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      window.scrollTo(0, 0);
      gsap.killTweensOf(window);

      timeoutRef.current = setTimeout(() => {
        smoothScrollToId(newHash, isMobile ? 150 : 0);
      }, 50);
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
}
